import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  AdjustmentsIcon,
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  TableIcon,
} from '@heroicons/react/outline';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { getIsSidebarOpen } from '../../../store/ui/drawer/selectors';
import { setIsSidebarOpen } from '../../../store/ui/drawer/actions';
import Button from '../../../components/Button';
import ToggleButtonGroup from '../../../components/ToggleButtonGroup';

const Sidebar = ({ mode, onSetMode, statistics, filters }) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const isSidebarOpen = useSelector(getIsSidebarOpen);
  const toggleSidebar = useCallback(
    () => dispatch(setIsSidebarOpen(!isSidebarOpen)),
    [dispatch, isSidebarOpen],
  );

  const options = [
    {
      label: t('table'),
      value: 'table',
      icon: TableIcon,
    },
    {
      label: t('calendar'),
      value: 'calendar',
      icon: CalendarIcon,
    },
  ];

  return (
    <div
      data-testid="sidebar"
      className={`${
        isSidebarOpen ? 'w-80 flex-shrink-0' : ''
      } bg-surface stack-0`}
    >
      <div
        className={`cluster-4  items-center border-b border-divider py-2 min-h-16 ${
          isSidebarOpen ? 'justify-between px-4' : 'w-16 justify-center px-2'
        }`}
      >
        {isSidebarOpen && (
          <div className="cluster-2 items-center">
            <AdjustmentsIcon className="w-5 h-5 flex-shrink-0" />
            <span className="font-medium whitespace-nowrap">
              {t('filtersAndOptions')}
            </span>
          </div>
        )}
        <Button
          data-testid="button-toggle-sidebar"
          type="tertiary"
          className="flex-shrink-0"
          icon={isSidebarOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          onClick={toggleSidebar}
        />
      </div>

      {/* Content */}
      {isSidebarOpen ? (
        <div className="p-4 stack-6 border-r border-divider flex-1 overflow-y-auto">
          {/* {children} */}
          {mode && (
            <>
              <div data-testid="sidebar-view-mode" className="stack-4">
                <span className="text-sm font-medium">{t('viewMode')}</span>
                <ToggleButtonGroup
                  value={mode}
                  options={options}
                  onChange={onSetMode}
                />
              </div>
              <hr className="border-divider" />
            </>
          )}
          {statistics && (
            <>
              <div data-testid="sidebar-quick-statistics" className="stack-4">
                <span className="text-sm font-medium">
                  {t('quickStatistics')}
                </span>
                {statistics}
              </div>
              <hr className="border-divider" />
            </>
          )}
          {filters && (
            <>
              <div data-testid="sidebar-filters" className="stack-4">
                <span className="text-sm font-medium">{t('filters')}</span>
                {filters}
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="flex-1 border-r border-divider" />
      )}
    </div>
  );
};

Sidebar.propTypes = {
  mode: PropTypes.oneOf(['table', 'calendar']),
  onSetMode: PropTypes.func,
  statistics: PropTypes.node,
  filters: PropTypes.node,
};

Sidebar.defaultProps = {
  mode: null,
  onSetMode: () => {},
  statistics: null,
  filters: null,
};

export default Sidebar;
